import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiError, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserPlus, PencilSimple, Trash } from "@phosphor-icons/react";

const inputCls = "bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 focus:border-emerald-600 focus-visible:ring-emerald-600";
const label = "text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block";

export default function TeamMembers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [roleItems, setRoleItems] = useState([]);
  const [managers, setManagers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [form, setForm] = useState(blank());

  function blank() {
    return { name: "", email: "", password: "Welcome@123", role: "member", employee_id: "", mobile: "", whatsapp: "", department: "", designation: "", reporting_manager: "", office_location: "", working_hours: "", reminder_preference: "email" };
  }

  const load = () => {
    api.get(`/users${search ? `?search=${search}` : ""}`).then((r) => { setUsers(r.data); setManagers(r.data.filter((u) => u.role !== "member")); });
    api.get("/departments").then((r) => setDepts(r.data));
    api.get("/roles").then((r) => setRoleItems(r.data)).catch(() => {});
  };
  useEffect(load, [search]);

  const roleOpts = (roleItems.length ? roleItems.map((r) => r.name) : ["Member", "Manager", "Admin"]).map((n) => ({ value: (n || "").toLowerCase(), label: n }));

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openAdd = () => { setEditingId(null); setForm(blank()); setOpen(true); };
  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({ ...blank(), ...u, password: "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    try {
      if (editingId) {
        const { email, id, ...rest } = form;
        if (!rest.password) delete rest.password;
        await api.put(`/users/${editingId}`, rest);
        toast.success("Member updated");
      } else {
        await api.post("/users", form);
        toast.success("Team member added");
      }
      setOpen(false); setForm(blank()); setEditingId(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { is_active: !u.is_active });
    toast.success(u.is_active ? "Deactivated" : "Activated"); load();
  };

  const del = async (u) => {
    if (u.id === me.id) return toast.error("You cannot delete your own account");
    if (!window.confirm(`Delete ${u.name}? This permanently removes the member.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Member deleted");
      setSelected((s) => { const n = new Set(s); n.delete(u.id); return n; });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const bulkDelete = async () => {
    const ids = [...selected].filter((id) => id !== me.id);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected member(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/users/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} member(s)`);
      setSelected(new Set()); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const selectableIds = users.filter((u) => u.id !== me.id).map((u) => u.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const Sel = ({ value, onChange, items, placeholder, testid }) => (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className={inputCls} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
        {items.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] animate-fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-1">Masters</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-zinc-50">Team Members</h1>
        </div>
        <div className="flex gap-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className={`${inputCls} w-48`} data-testid="member-search" />
          <Button onClick={openAdd} data-testid="add-member-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-semibold gap-1.5"><UserPlus weight="bold" size={16} /> Add Member</Button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between mb-3 border border-emerald-600/40 bg-emerald-600/5 px-4 py-2.5 rounded-sm" data-testid="bulk-bar">
          <span className="text-sm text-zinc-200">{selected.size} member(s) selected</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} className="border-zinc-700 bg-zinc-950 text-zinc-300 rounded-sm text-xs">Clear</Button>
            <Button size="sm" onClick={bulkDelete} data-testid="bulk-delete-btn" className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs gap-1"><Trash size={14} /> Delete selected</Button>
          </div>
        </div>
      )}

      <div className="border border-zinc-800 bg-zinc-900/30 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800 text-left text-[10px] uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-3 w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="select-all" /></th>
              <th className="px-4 py-3 font-bold">Emp ID</th><th className="px-4 py-3 font-bold">Name</th>
              <th className="px-4 py-3 font-bold">Role</th><th className="px-4 py-3 font-bold">Department</th>
              <th className="px-4 py-3 font-bold">Mobile</th><th className="px-4 py-3 font-bold">Email</th>
              <th className="px-4 py-3 font-bold">Status</th><th className="px-4 py-3 font-bold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {users.map((u) => (
              <tr key={u.id} className={`hover:bg-zinc-900 transition-colors ${selected.has(u.id) ? "bg-emerald-600/5" : ""}`} data-testid={`member-row-${u.id}`}>
                <td className="px-4 py-3">
                  <Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggleOne(u.id)} disabled={u.id === me.id} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid={`select-${u.id}`} />
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-zinc-500">{u.employee_id}</td>
                <td className="px-4 py-3 text-zinc-100">{u.name}<div className="text-xs text-zinc-500">{u.designation}</div></td>
                <td className="px-4 py-3 capitalize text-zinc-300">{u.role}</td>
                <td className="px-4 py-3 text-zinc-400">{u.department || "—"}</td>
                <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{u.mobile || "—"}</td>
                <td className="px-4 py-3 text-zinc-400 text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={u.is_active !== false} onCheckedChange={() => toggleActive(u)} className="data-[state=checked]:bg-emerald-600" data-testid={`toggle-${u.id}`} />
                    <span className={`text-xs ${u.is_active !== false ? "text-emerald-400" : "text-zinc-500"}`}>{u.is_active !== false ? "Active" : "Inactive"}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(u)} data-testid={`edit-${u.id}`} title="Edit" className="p-1.5 text-zinc-400 hover:text-emerald-400 transition-colors"><PencilSimple size={16} /></button>
                    <button onClick={() => del(u)} data-testid={`delete-${u.id}`} title="Delete" disabled={u.id === me.id} className="p-1.5 text-zinc-400 hover:text-red-400 transition-colors disabled:opacity-30 disabled:hover:text-zinc-400"><Trash size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "Edit Team Member" : "Add Team Member"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto pr-1">
            <div><label className={label}>Name *</label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="mf-name" /></div>
            <div><label className={label}>Employee ID</label><Input value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} className={inputCls} placeholder="Auto" /></div>
            <div><label className={label}>Email *</label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} disabled={!!editingId} className={`${inputCls} disabled:opacity-60`} data-testid="mf-email" /></div>
            <div><label className={label}>{editingId ? "New Password (leave blank to keep)" : "Password"}</label><Input value={form.password} onChange={(e) => set("password", e.target.value)} className={inputCls} placeholder={editingId ? "Unchanged" : ""} /></div>
            <div><label className={label}>Mobile</label><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className={inputCls} /></div>
            <div><label className={label}>WhatsApp</label><Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} /></div>
            <div><label className={label}>Role</label><Sel value={form.role} onChange={(v) => set("role", v)} items={roleOpts} placeholder="Role" testid="mf-role" /><p className="text-[10px] text-zinc-500 mt-1">Add roles in Masters → Team Roles. Access to tabs is set per person in Administration → Permissions.</p></div>
            <div><label className={label}>Department</label><Sel value={form.department} onChange={(v) => set("department", v)} items={depts.map((d) => ({ value: d.name, label: d.name }))} placeholder="Select" /></div>
            <div><label className={label}>Designation</label><Input value={form.designation} onChange={(e) => set("designation", e.target.value)} className={inputCls} /></div>
            <div><label className={label}>Reporting Manager</label><Sel value={form.reporting_manager} onChange={(v) => set("reporting_manager", v)} items={managers.map((m) => ({ value: m.id, label: m.name }))} placeholder="Select" /></div>
            <div><label className={label}>Office Location</label><Input value={form.office_location} onChange={(e) => set("office_location", e.target.value)} className={inputCls} /></div>
            <div><label className={label}>Working Hours</label><Input value={form.working_hours} onChange={(e) => set("working_hours", e.target.value)} className={inputCls} placeholder="9:00-18:00" /></div>
            <div><label className={label}>Reminder Preference</label><Sel value={form.reminder_preference} onChange={(v) => set("reminder_preference", v)} items={[{ value: "email", label: "Email" }, { value: "whatsapp", label: "WhatsApp" }, { value: "both", label: "Both" }]} placeholder="Pref" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="mf-save">{editingId ? "Save Changes" : "Add Member"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
